import {
  RoundedBox,
  Severity,
  SnackbarMessageProps,
  snackbarStore,
  useIsFilterApplied,
} from "@knowfully/ui.common";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Box, Tooltip } from "@mui/material";
import {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
  LazyTable,
} from "@knowfully/ui.common";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import {
  EnrollAllUsersRequest,
  EnrollmentUsersFilter,
  EnrollUsersRequest,
  IResponse,
  ListRequestOfEnrollmentUsersFilter,
  ResponseCode,
} from "services/organizationPortalClient";
import contextStore from "stores/contextStore";
import translateError from "utils/translateError";
import EnrollmentUserFilter, { emptyFilter } from "./EnrollmentUserFilter";

type EnrollmentUsersProps = {
  skuId: string;
};

export default function EnrollmentUsers({
  skuId,
}: Readonly<EnrollmentUsersProps>) {
  const { formatMessage: t } = useIntl();
  const { enrollmentService, courseCatalogUrl } = useGlobalContext();
  const navigate = useNavigate();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("lastName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const queryClient = useQueryClient();
  const { organization } = contextStore.useState();
  const [filter, setFilter] = useState(emptyFilter);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { data, isFetching } = useQuery(
    [
      "enrollment-candidates",
      organization?.id,
      skuId,
      order,
      orderBy,
      page,
      rowsPerPage,
      filter,
    ],
    () =>
      enrollmentService.getEnrollmentUsers(
        ListRequestOfEnrollmentUsersFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: { ...filter, skuId: skuId },
        })
      ),
    { keepPreviousData: true }
  );

  // Only the total record count is used, so a single-row page is enough. The count cannot be
  // derived from the table itself: the current page can be fully enrolled while later pages are not.
  // The filter is built through its generated class so a missing excludeEnrolled fails to compile
  // instead of being dropped silently on the way to the service.
  const { data: enrollableUsers } = useQuery(
    ["enrollment-candidates-enrollable", organization?.id, skuId],
    () => {
      const parameters = new EnrollmentUsersFilter();
      parameters.skuId = skuId;
      parameters.excludeEnrolled = true;

      return enrollmentService.getEnrollmentUsers(
        ListRequestOfEnrollmentUsersFilter.fromJS({
          pageNumber: 0,
          pageSize: 1,
          parameters: parameters,
        })
      );
    }
  );

  useEffect(() => {
    setPage(0);
  }, [filter]);

  const isFilterApplied = useIsFilterApplied(filter, []);
  const isRegisterAllMode = !isFilterApplied && selectedIds.length === 0;
  const hasEnrollableUsers = (enrollableUsers?.totalRecords ?? 0) > 0;

  const runEnrollment = async (
    enroll: () => Promise<IResponse>,
    successMessageId: string
  ) => {
    try {
      let result = await enroll();

      result.responseCode === ResponseCode.OK
        ? snackbarStore.update((s) => {
            s.snackbarMessage = new SnackbarMessageProps(
              t({ id: successMessageId }),
              Severity.Success
            );
          })
        : snackbarStore.update((s) => {
            s.snackbarMessage = new SnackbarMessageProps(
              t({ id: "usersRegisteredError" }),
              Severity.Error
            );
          });

      queryClient.invalidateQueries(["enrollment-candidates"]);
      // Not covered by the key above: prefix matching compares whole key segments.
      queryClient.invalidateQueries(["enrollment-candidates-enrollable"]);
      queryClient.invalidateQueries(["enrollments"]);

      // Leaving the page avoids showing a list that cannot reflect the all-users job yet, which
      // reads as nothing having been registered. The snackbar is mounted above the routes, so it
      // survives the navigation.
      if (result.responseCode === ResponseCode.OK) {
        navigate(courseCatalogUrl());
      }
    } catch (err: unknown) {
      snackbarStore.update((s) => {
        s.snackbarMessage = new SnackbarMessageProps(
          translateError(err, t),
          Severity.Error
        );
      });
    }
  };

  const executeEnrollment = (userIds: string[]) =>
    runEnrollment(
      () =>
        enrollmentService.enrollUsers(
          EnrollUsersRequest.fromJS({
            skuId: skuId,
            userIds: userIds,
          })
        ),
      "usersRegisteredInfo"
    );

  // Registering every user runs as a back-end job, so this returns before anyone is enrolled and
  // the confirmation says the registration started rather than that it finished.
  const executeEnrollmentForAllUsers = () =>
    runEnrollment(
      () =>
        enrollmentService.enrollAllUsers(
          EnrollAllUsersRequest.fromJS({
            skuId: skuId,
          })
        ),
      "registerAllUsersQueuedInfo"
    );

  const cells: Array<TableCellType> = [
    {
      accessor: "lastName",
      type: "text",
      label: t({ id: "lastName" }),
    },
    {
      accessor: "firstName",
      type: "text",
      label: t({ id: "firstName" }),
    },
    {
      accessor: "email",
      type: "text",
      label: t({ id: "email" }),
    },
    {
      accessor: "",
      type: "node",
      noSort: true,
      label: "",
      View: ({ value }) =>
        value.hasPriorCompletion ? (
          <Tooltip title={t({ id: "userPreviouslyCompletedTooltip" })}>
            <TaskAltIcon fontSize="small" color="action" />
          </Tooltip>
        ) : null,
    },
    {
      accessor: "accessLevelName",
      type: "text",
      noSort: true,
      label: t({ id: "levelOfAccess" }),
    },
  ];

  return (
    <>
      <EnrollmentUserFilter onChange={setFilter} />
      <RoundedBox>
        <LazyTable
          {...{
            "aria-label": t({ id: "enrollUsers" }),
            loading: isFetching,
            rowCount: data?.totalRecords ?? 0,
            rows:
              data?.data?.map(
                (user) =>
                  ({
                    ...user,
                    isdisabledForSelection: user.isEnrolled,
                    isPreselected: user.isEnrolled,
                  }) as LazyTableRow
              ) ?? [],
            cells,
            page,
            setPage,
            order,
            setOrder,
            orderBy,
            setOrderBy,
            rowsPerPage,
            setRowsPerPage,
            titleComponent: <Box />,
            resetSelectionKey: filter,
            executeMultiple: {
              onExecuteMultiple: async (ids: string[]) => {
                await (isRegisterAllMode
                  ? executeEnrollmentForAllUsers()
                  : executeEnrollment(ids));
              },
              onSelectionChange: setSelectedIds,
              dialogTitle: isRegisterAllMode
                ? t({ id: "registerAllUsersDialogTitle" })
                : t({ id: "registerUsersDialogTitle" }),
              dialogLabel: isRegisterAllMode
                ? t({ id: "registerAllUsersWarning" })
                : selectedIds.some(
                      (id) =>
                        data?.data?.find((u) => u.id === id)?.hasPriorCompletion === true,
                    )
                  ? `${t({ id: "registerUsersWarning" })}\n\n${t({ id: "registerUsersPriorCompletionWarning" })}`
                  : t({ id: "registerUsersWarning" }),
              buttonLabel: isRegisterAllMode
                ? t({ id: "registerAllUsersButton" })
                : t({ id: "registerUsersButtonFormat" }),
              buttonLabelSelectedCountFormatKey: isRegisterAllMode
                ? undefined
                : t({ id: "registerUsersSelectedCountFormatKey" }),
              allowEmptySelection: isRegisterAllMode && hasEnrollableUsers,
              disabledForSelectionTooltip: t({
                id: "userAlreadyRegisteredTooltip",
              }),
              alertSeverity: "warning",
            },
          }}
        />
      </RoundedBox>
    </>
  );
}
